import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  ExternalLink,
  Download,
} from "lucide-react";
import { Citation } from "@/types/chat.types";

interface CitationCardProps {
  citation: Citation;
}

// The per-chunk relevance band (question↔chunk embedding similarity) is
// deliberately NOT shown here: for short entity-style questions ("Tell me
// about X", a part number) every supporting chunk scores "Low", which reads
// as "dubious source" under a High Confidence answer. The excerpt is the
// evidence; the band stays available for analytics.

const CitationCard: React.FC<CitationCardProps> = ({ citation }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      id={`citation-${citation.citationNumber}`}
      className="border border-subtle rounded-lg p-4 bg-surface scroll-mt-4"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-semibold">
              {citation.citationNumber}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-text-faint flex-shrink-0" />
              <h4 className="text-sm font-medium text-text-base truncate">
                {citation.documentFilename || citation.documentTitle}
              </h4>
            </div>
            <div className="text-xs text-text-faint">
              {citation.chunkType === "figure"
                ? `Figure · page ${citation.pageNumber ?? "?"}`
                : `Chunk ${citation.chunkIndex + 1}`}
            </div>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-shrink-0 ml-2 p-1 hover:bg-surface-2 rounded transition-colors"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-text-muted" />
          ) : (
            <ChevronDown className="w-5 h-5 text-text-muted" />
          )}
        </button>
      </div>

      {/* Relevant Text (Always Visible) */}
      <div className="mb-3">
        <p className="text-xs font-medium text-text-muted mb-1.5">
          Relevant excerpt:
        </p>
        <div className="bg-warn-bg border border-warn rounded p-3">
          <p className="text-sm text-text-base leading-relaxed">
            {citation.relevantText}
          </p>
        </div>
      </div>

      {/* Full Chunk Text (Expandable) */}
      {isExpanded && (
        <div>
          <p className="text-xs font-medium text-text-muted mb-1.5">
            Full context:
          </p>
          <div className="bg-surface-2 border border-subtle rounded p-3 max-h-64 overflow-y-auto">
            <p className="text-sm text-text-muted leading-relaxed whitespace-pre-wrap">
              {citation.fullChunkText}
            </p>
          </div>

          {/* Document Actions */}
          <div className="mt-3 pt-3 border-t border-subtle flex flex-wrap gap-4">
            <a
              href={`/documents/${citation.documentId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent-strong transition-colors"
              aria-label={`View full document: ${citation.documentFilename || citation.documentTitle}`}
            >
              <span>View document</span>
              <ExternalLink className="w-4 h-4" />
            </a>
            {citation.downloadUrl && (
              <a
                href={citation.downloadUrl}
                download={citation.documentFilename}
                className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent-strong transition-colors"
                aria-label={`Download: ${citation.documentFilename || citation.documentTitle}`}
              >
                <span>Download PDF</span>
                <Download className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CitationCard;
